import { Fragment, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Eye, PackageCheck } from 'lucide-react';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { listArticulosImportacion, type ArticuloImportacion } from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { DetalleRecepcionDialog } from '@/features/recepcion/components/DetalleRecepcionDialog';
import type { RecepcionRow } from '@/features/recepcion/api';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { cn } from '@/shared/lib/cn';
import { fechaImp } from './componentes';

const cantidad = (n: number) => n.toLocaleString('es-AR', { maximumFractionDigits: 2 });

/** Los artículos del contenedor. La usa también la pestaña para mostrar el contador. */
export function useArticulosImportacion(importacionId: number) {
  const { config: activeCountry } = useActiveCountry();
  return useQuery({
    queryKey: ['importaciones', activeCountry.code, 'articulos', importacionId],
    queryFn: () => listArticulosImportacion(importacionId),
    staleTime: 15_000,
  });
}

/**
 * Qué trae el contenedor: los artículos de todas sus compras sumados, con lo
 * comprado, lo recibido y lo que falta, para no tener que abrir compra por
 * compra. Sólo lectura: las cantidades se cargan en la compra y se reciben en
 * Recepciones.
 */
export function ArticulosImportacion({ importacionId }: { importacionId: number }) {
  const q = useArticulosImportacion(importacionId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const readOnly = useIsReadOnly();
  // Recibir desde acá abre el MISMO diálogo que Recepciones: una sola forma de recibir.
  const [recibiendo, setRecibiendo] = useState<RecepcionRow | null>(null);
  const abrirRecepcion = (d: ArticuloImportacion['detalle'][number]) =>
    d.recepcionId &&
    setRecibiendo({
      id: d.recepcionId,
      fields: { Status_RR: d.recepcionStatus ?? 'Pendiente' },
      compra: {
        id: d.compraId,
        proveedor: d.proveedor,
        fechaFactura: d.fecha ?? '',
        nroFactura: [d.comprobante, d.numero].filter(Boolean).join(' '),
        statusCO: '',
      },
    });
  // Artículos abiertos: cada uno muestra de qué compras sale su cantidad.
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const alternar = (clave: string) =>
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });

  if (q.isPending) return <Skeleton className="h-32 w-full rounded-2xl" />;
  if (q.isError) {
    return <p className="text-sm text-destructive">No se pudieron cargar los artículos.</p>;
  }
  if (q.data.length === 0) {
    return (
      <p className="rounded-lg border border-dashed bg-card/40 px-4 py-6 text-center text-sm text-muted-foreground">
        Todavía no hay artículos. Aparecen al cargar las compras del fabricante con sus artículos y cantidades.
      </p>
    );
  }

  const items = q.data;
  const total = items.reduce(
    (t, a) => ({ comprado: t.comprado + a.comprado, recibido: t.recibido + a.recibido, pendiente: t.pendiente + a.pendiente }),
    { comprado: 0, recibido: 0, pendiente: 0 },
  );
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Abrí un artículo para ver de qué compras sale y recibir lo pendiente.
      </p>
      <div className="overflow-x-auto rounded-2xl border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Artículo</th>
              <th className="px-4 py-2 text-right font-medium">Compras</th>
              <th className="px-4 py-2 text-right font-medium">Comprado</th>
              <th className="px-4 py-2 text-right font-medium">Recibido</th>
              <th className="px-4 py-2 text-right font-medium">Pendiente</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((a) => {
              const clave = a.articleId != null ? String(a.articleId) : `t:${a.articulo}`;
              const abierto = abiertos.has(clave);
              return (
                <Fragment key={clave}>
                  <tr
                    className="cursor-pointer hover:bg-secondary/50"
                    onClick={() => alternar(clave)}
                    aria-expanded={abierto}
                    title={abierto ? 'Ocultar las compras' : 'Ver de qué compras sale'}
                  >
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <ChevronRight
                          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', abierto && 'rotate-90')}
                          aria-hidden
                        />
                        <span className="font-medium text-foreground">{a.articulo}</span>
                        {a.codigo && <span className="text-xs text-muted-foreground">{a.codigo}</span>}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{a.compras}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{cantidad(a.comprado)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{cantidad(a.recibido)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      <Pendiente valor={a.pendiente} />
                    </td>
                  </tr>
                  {abierto &&
                    a.detalle.map((d) => (
                      <tr key={`${clave}-${d.compraId}`} className="bg-muted/30 text-xs">
                        <td className="py-2 pl-11 pr-4">
                          <p className="font-medium text-foreground">{d.proveedor}</p>
                          <p className="text-muted-foreground">
                            {[d.comprobante, d.numero].filter(Boolean).join(' ') || '—'} · {fechaImp(d.fecha)}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-right">
                          {!readOnly && d.pendiente > 0 && d.recepcionId && d.recepcionStatus === 'Pendiente' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 h-7 gap-1 px-2 text-xs"
                              onClick={() => abrirRecepcion(d)}
                              title="Cargar el remito de lo que llegó (mismo diálogo que Recepciones)"
                            >
                              <PackageCheck className="h-3.5 w-3.5" aria-hidden /> Recibir
                            </Button>
                          )}
                          <button
                            type="button"
                            onClick={() => navigate(`/compras/${d.compraId}`)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
                            title="Ver compra"
                            aria-label="Ver compra"
                          >
                            <Eye className="h-4 w-4" aria-hidden />
                          </button>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{cantidad(d.comprado)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{cantidad(d.recibido)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          <Pendiente valor={d.pendiente} />
                        </td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot className="border-t bg-muted/40 font-semibold">
            <tr>
              <td className="px-4 py-2">
                Total{' '}
                <span className="font-normal text-muted-foreground">
                  ({items.length} {items.length === 1 ? 'artículo' : 'artículos'})
                </span>
              </td>
              <td className="px-4 py-2" />
              <td className="px-4 py-2 text-right tabular-nums">{cantidad(total.comprado)}</td>
              <td className="px-4 py-2 text-right tabular-nums">{cantidad(total.recibido)}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                <Pendiente valor={total.pendiente} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {recibiendo && (
        <DetalleRecepcionDialog
          summary={recibiendo}
          onClose={() => {
            setRecibiendo(null);
            // Lo recibido cambia los artículos, la mercadería y el stock.
            qc.invalidateQueries({ queryKey: ['importaciones'] });
            qc.invalidateQueries({ queryKey: ['inventario'] });
          }}
        />
      )}
    </div>
  );
}

/** Lo que falta llegar en ámbar; completo en verde. */
function Pendiente({ valor }: { valor: number }) {
  return valor > 0 ? (
    <span className="font-semibold text-amber-700">{cantidad(valor)}</span>
  ) : (
    <span className="text-xs font-semibold text-emerald-700">Completo</span>
  );
}
